/**
 * APIKeysPage - ENTERPRISE API Key Management
 * 
 * Features:
 * - Create new API keys with name and scope
 * - List all keys with usage stats
 * - Revoke keys
 * - View key details and activity
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useFeatures } from '../hooks/useFeatures';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Key, Plus, Trash2, Copy, Check, Eye, EyeOff, 
  Shield, Edit, Activity, AlertTriangle, Loader2 
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Scope descriptions
const SCOPE_INFO = {
  read_only: {
    label: 'Read Only',
    description: 'Can only read data (GET requests)',
    icon: Eye,
    color: 'text-blue-600'
  },
  read_write: {
    label: 'Read & Write',
    description: 'Can read and create/update data',
    icon: Edit,
    color: 'text-green-600'
  },
  admin: {
    label: 'Admin',
    description: 'Full access including admin operations',
    icon: Shield,
    color: 'text-purple-600'
  }
};

const APIKeysPage = ({ onBack }) => {
  const { getAccessToken } = useAuth();
  const { has } = useFeatures();
  
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKey, setNewKey] = useState(null);  // Newly created key (shown once)
  const [copiedKey, setCopiedKey] = useState(false);
  
  // Create form state
  const [createForm, setCreateForm] = useState({
    name: '',
    scope: 'read_only',
    expires_in_days: null
  });
  const [creating, setCreating] = useState(false);

  // Check feature access
  const hasAccess = has('api_access');

  // Fetch keys
  const fetchKeys = useCallback(async () => {
    if (!hasAccess) return;
    
    try {
      const token = getAccessToken();
      const res = await fetch(`${API}/api-keys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setKeys(data);
      }
    } catch (err) {
      console.error('Error fetching API keys:', err);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, hasAccess]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  // Create key
  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const token = getAccessToken();
      const res = await fetch(`${API}/api-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: createForm.name,
          scope: createForm.scope,
          expires_in_days: createForm.expires_in_days || null
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create API key');
      }

      // Show the new key (only shown once)
      setNewKey(data);
      setShowCreateModal(false);
      setCreateForm({ name: '', scope: 'read_only', expires_in_days: null });
      
      // Refresh list
      fetchKeys();
      toast.success('API key created successfully');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  // Revoke key
  const handleRevoke = async (keyId, keyName) => {
    if (!window.confirm(`Are you sure you want to revoke "${keyName}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      const token = getAccessToken();
      const res = await fetch(`${API}/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to revoke key');
      }

      toast.success('API key revoked');
      fetchKeys();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Copy key to clipboard
  const copyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
      toast.success('API key copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy key');
    }
  };

  // Feature gate - show upgrade prompt
  if (!hasAccess) {
    return (
      <div className="page api-keys-page" data-testid="api-keys-page">
        <div className="page-header">
          <h1>API Keys</h1>
          {onBack && <Button variant="outline" onClick={onBack}>← Back</Button>}
        </div>
        
        <Card className="feature-gate-card">
          <div className="feature-gate-content">
            <Key size={48} className="feature-gate-icon" />
            <h2>ENTERPRISE Feature</h2>
            <p>
              API key management allows you to integrate RISKEE with your existing systems.
              This feature is available on the Enterprise plan.
            </p>
            <Button 
              variant="default" 
              onClick={() => window.dispatchEvent(new CustomEvent('navigate-billing'))}
            >
              View Enterprise Plans
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-loading">
        <Loader2 className="animate-spin" size={32} />
        <p>Loading API keys...</p>
      </div>
    );
  }

  return (
    <div className="page api-keys-page" data-testid="api-keys-page">
      <div className="page-header">
        <div>
          <h1>API Keys</h1>
          <p>Manage programmatic access to your RISKEE data</p>
        </div>
        <div className="page-actions">
          {onBack && <Button variant="outline" onClick={onBack}>← Back</Button>}
          <Button variant="default" onClick={() => setShowCreateModal(true)}>
            <Plus size={18} /> Create Key
          </Button>
        </div>
      </div>

      {/* New Key Display (shown once after creation) */}
      {newKey && (
        <Card className="new-key-card" data-testid="new-key-display">
          <div className="new-key-header">
            <AlertTriangle size={20} className="warning-icon" />
            <h3>Save Your API Key</h3>
          </div>
          <p className="new-key-warning">
            This key will only be shown once. Copy it now and store it securely.
          </p>
          <div className="new-key-value">
            <code>{newKey.key}</code>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => copyKey(newKey.key)}
              data-testid="copy-key-btn"
            >
              {copiedKey ? <Check size={16} /> : <Copy size={16} />}
              {copiedKey ? 'Copied!' : 'Copy'}
            </Button>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setNewKey(null)}
            className="dismiss-new-key"
          >
            I've saved this key
          </Button>
        </Card>
      )}

      {/* Keys List */}
      <div className="api-keys-list">
        {keys.length === 0 ? (
          <Card className="empty-state-card">
            <Key size={48} />
            <h3>No API Keys</h3>
            <p>Create your first API key to integrate with external systems.</p>
            <Button variant="default" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} /> Create Your First Key
            </Button>
          </Card>
        ) : (
          keys.map(key => {
            const scopeInfo = SCOPE_INFO[key.scope] || SCOPE_INFO.read_only;
            const ScopeIcon = scopeInfo.icon;
            
            return (
              <Card 
                key={key.id} 
                className={`api-key-card ${!key.is_active ? 'revoked' : ''}`}
                data-testid={`api-key-${key.id}`}
              >
                <div className="key-header">
                  <div className="key-info">
                    <h4>{key.name}</h4>
                    <code className="key-prefix">{key.prefix}</code>
                  </div>
                  <div className={`key-scope ${scopeInfo.color}`}>
                    <ScopeIcon size={16} />
                    <span>{scopeInfo.label}</span>
                  </div>
                </div>
                
                <div className="key-meta">
                  <span>Created: {new Date(key.created_at).toLocaleDateString()}</span>
                  {key.expires_at && (
                    <span>Expires: {new Date(key.expires_at).toLocaleDateString()}</span>
                  )}
                  {key.last_used_at && (
                    <span>Last used: {new Date(key.last_used_at).toLocaleString()}</span>
                  )}
                </div>
                
                <div className="key-actions">
                  {key.is_active ? (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => handleRevoke(key.id, key.name)}
                      data-testid={`revoke-${key.id}`}
                    >
                      <Trash2 size={16} /> Revoke
                    </Button>
                  ) : (
                    <span className="revoked-badge">Revoked</span>
                  )}
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* Create Key Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal api-key-modal" onClick={e => e.stopPropagation()}>
            <h3>Create API Key</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Key Name *</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={e => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Production Integration"
                  required
                  maxLength={100}
                />
              </div>
              
              <div className="form-group">
                <label>Access Scope *</label>
                <div className="scope-options">
                  {Object.entries(SCOPE_INFO).map(([value, info]) => {
                    const Icon = info.icon;
                    return (
                      <label 
                        key={value} 
                        className={`scope-option ${createForm.scope === value ? 'selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name="scope"
                          value={value}
                          checked={createForm.scope === value}
                          onChange={e => setCreateForm(prev => ({ ...prev, scope: e.target.value }))}
                        />
                        <Icon size={20} className={info.color} />
                        <div>
                          <strong>{info.label}</strong>
                          <span>{info.description}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
              
              <div className="form-group">
                <label>Expiration (optional)</label>
                <select
                  value={createForm.expires_in_days || ''}
                  onChange={e => setCreateForm(prev => ({ 
                    ...prev, 
                    expires_in_days: e.target.value ? parseInt(e.target.value) : null 
                  }))}
                >
                  <option value="">Never expires</option>
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                  <option value="180">180 days</option>
                  <option value="365">1 year</option>
                </select>
              </div>

              <div className="form-actions">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="default" disabled={creating || !createForm.name}>
                  {creating ? <Loader2 className="animate-spin" size={18} /> : <Key size={18} />}
                  {creating ? 'Creating...' : 'Create Key'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default APIKeysPage;
